
import React, { useMemo, useState } from 'react';
import { 
  Users, Trello, FileText, AlertCircle, TrendingUp, Clock, ArrowUpRight, History, ArrowRight, CheckCircle2, PlusCircle, BarChart3, Calendar, Gift, MessageCircle, Trophy, Star, Award, Target, TrendingDown, Minus, Activity, AlertTriangle, UserCheck, FilePlus, HelpCircle, Info, CheckSquare, MessageSquare
} from 'lucide-react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { TaskSector, TaskStatus, Task, Contact, Document, Attendance, AttendanceStatus, User, UserRole, Container, AuditEvent } from '../types';

interface DashboardProps {
  tasks: Task[];
  contacts: Contact[];
  letters: Document[];
  attendances: Attendance[];
  users: User[];
  setActiveTab: (tab: any) => void;
  currentUser: User;
  isDarkMode: boolean;
  onOpenBirthday: (contact: Contact) => void;
  container?: Container | null;
  auditLogs?: AuditEvent[];
}

const Dashboard: React.FC<DashboardProps> = ({ tasks, contacts, letters, attendances, users, setActiveTab, currentUser, isDarkMode, onOpenBirthday, container, auditLogs = [] }) => {
  const [timeRange, setTimeRange] = useState<'7' | '14' | '30'>('7');
  const [showScoreInfo, setShowScoreInfo] = useState(false);

  const isLeadership = useMemo(() => {
    return currentUser?.role?.toLowerCase() === 'leadership';
  }, [currentUser?.role]);

  // Helper para calcular a data limite baseada no timeRange
  const limitDate = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - parseInt(timeRange));
    return d;
  }, [timeRange]);

  // 1. Lógica do Gráfico: Volume de Atividade por Dia
  const chartData = useMemo(() => {
    const daysCount = parseInt(timeRange);
    const data = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Debug local para o console caso esteja vazio
    if (auditLogs.length === 0) {
      console.warn("[Dashboard] Nenhum log de auditoria recebido para o gráfico.");
    }

    for (let i = daysCount - 1; i >= 0; i--) {
      const targetDate = new Date();
      targetDate.setDate(today.getDate() - i);
      targetDate.setHours(0, 0, 0, 0);
      
      const dateStr = targetDate.toISOString().split('T')[0];
      const dayLabel = targetDate.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
      
      const count = (auditLogs || []).filter(log => {
        // Aceita 'timestamp' ou 'created_at' como campos de data
        const logDateRaw = log?.timestamp || (log as any)?.created_at;
        if (!logDateRaw) return false;

        const matchesDate = logDateRaw.startsWith(dateStr);
        if (isLeadership) return matchesDate && log.user_id === currentUser?.id;
        return matchesDate;
      }).length;

      data.push({ 
        name: dayLabel, 
        volume: count, 
        fullDate: targetDate.toLocaleDateString('pt-BR') 
      });
    }
    return data;
  }, [auditLogs, timeRange, isLeadership, currentUser?.id]);

  // 2. Produtividade da Equipe
  const teamPerformance = useMemo(() => {
    const targetUsers = isLeadership ? users.filter(u => u.id === currentUser?.id) : users;

    return targetUsers.map(user => {
      const createdContacts = (contacts || []).filter(c => 
        c.created_by === user.id && 
        c.created_at && new Date(c.created_at) >= limitDate
      ).length;

      const createdTasks = (tasks || []).filter(t => 
        (t.history || []).some(h => 
          h.userId === user.id && 
          h.action?.toLowerCase().includes('criou') && 
          new Date(h.timestamp) >= limitDate
        )
      ).length;

      const finalizedTasks = (tasks || []).filter(t => 
        (t.history || []).some(h => 
          h.userId === user.id && 
          (h.action?.toLowerCase().includes('concluiu') || h.action?.toLowerCase().includes('concluído')) && 
          new Date(h.timestamp) >= limitDate
        )
      ).length;

      const createdAttendances = (attendances || []).filter(a => 
        a.created_by === user.id && 
        a.created_at && new Date(a.created_at) >= limitDate
      ).length;

      const solvedAttendances = (attendances || []).filter(a => 
        a.responsible_id === user.id && 
        a.status === 'Concluído' &&
        a.created_at && new Date(a.created_at) >= limitDate
      ).length;

      const createdDocs = (letters || []).filter(l => 
        l.made_by_id === user.id && 
        l.date && new Date(l.date) >= limitDate
      ).length;
      
      const totalScore = createdContacts + createdTasks + finalizedTasks + createdAttendances + solvedAttendances + createdDocs;

      return {
        id: user.id,
        name: user.name || 'Usuário',
        score: totalScore,
        details: {
          contacts: createdContacts,
          attCreated: createdAttendances,
          attSolved: solvedAttendances,
          tasksCreated: createdTasks,
          tasksDone: finalizedTasks,
          docs: createdDocs
        }
      };
    }).sort((a, b) => b.score - a.score);
  }, [users, contacts, tasks, attendances, letters, limitDate, isLeadership, currentUser?.id]);

  // Derived recent activities from auditLogs
  const recentActivities = useMemo(() => {
    return (auditLogs || []).slice(0, 15);
  }, [auditLogs]);

  const stats = [
    { id: 'crm', label: 'Cidadãos', value: (contacts || []).length, icon: Users, color: 'text-blue-500' },
    { id: 'kanban', label: 'Tarefas Ativas', value: (tasks || []).filter(t => t.status !== TaskStatus.DONE).length, icon: Trello, color: 'text-indigo-500', hidden: isLeadership },
    { id: 'attendance', label: 'Atendimentos', value: (attendances || []).length, icon: MessageCircle, color: 'text-emerald-500' },
    { id: 'documents', label: 'Documentos', value: (letters || []).length, icon: FileText, color: 'text-amber-500', hidden: isLeadership },
  ];

  const pendingAttendancesCount = useMemo(() => {
    return (attendances || []).filter(a => a.status === AttendanceStatus.PENDING).length;
  }, [attendances]);

  const hasData = useMemo(() => chartData.some(d => d.volume > 0), [chartData]);

  return (
    <div className="space-y-10 animate-in fade-in duration-700 pb-20">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.filter(s => !s.hidden).map((stat) => (
          <div key={stat.id} className="bg-white dark:bg-navy-900 p-6 rounded-[32px] border border-neutral-100 dark:border-navy-800 shadow-sm flex items-center gap-5">
            <div className={`p-4 rounded-2xl bg-slate-50 dark:bg-navy-800 ${stat.color}`}><stat.icon size={24} /></div>
            <div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{stat.label}</p>
              <h3 className="text-3xl font-black dark:text-white tracking-tighter">{stat.value}</h3>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <div className="bg-white dark:bg-navy-900 p-8 rounded-[40px] border border-neutral-100 dark:border-navy-800 shadow-sm">
            <div className="flex justify-between items-center mb-8">
              <div>
                <h3 className="text-xl font-black text-navy-900 dark:text-white flex items-center gap-2 uppercase tracking-tighter">
                  <TrendingUp className="text-parliament-600" size={20} /> Evolução de Desempenho
                </h3>
                <p className="text-xs font-medium text-slate-400">Volume total de ações processadas no período</p>
              </div>
              <div className="flex bg-slate-100 dark:bg-navy-950 p-1 rounded-xl">
                {['7', '14', '30'].map(d => (
                  <button key={d} onClick={() => setTimeRange(d as any)} className={`px-4 py-1.5 rounded-lg text-[10px] font-black transition-all ${timeRange === d ? 'bg-white dark:bg-navy-800 text-parliament-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}>{d}D</button>
                ))}
              </div>
            </div>
            
            <div className="h-72 w-full relative">
              {!hasData && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/50 dark:bg-navy-900/50 backdrop-blur-[1px] rounded-3xl">
                   <Activity size={32} className="text-slate-300 mb-2" />
                   <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Aguardando dados de atividade</p>
                </div>
              )}
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorVol" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0084ff" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#0084ff" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDarkMode ? '#1e293b' : '#f1f5f9'} />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fontSize: 10, fontWeight: 700, fill: '#94a3b8'}} dy={10} />
                  <YAxis hide domain={[0, 'auto']} />
                  <Tooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="bg-navy-900 text-white p-3 rounded-xl shadow-2xl border border-white/10">
                            <p className="text-[10px] font-black uppercase opacity-60 mb-1">{payload[0].payload.fullDate}</p>
                            <p className="text-sm font-black">{payload[0].value} Ações registradas</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Area type="monotone" dataKey="volume" stroke="#0084ff" strokeWidth={4} fillOpacity={1} fill="url(#colorVol)" animationDuration={1500} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white dark:bg-navy-900 p-8 rounded-[40px] border border-neutral-100 dark:border-navy-800 shadow-sm relative overflow-visible">
            <div className="flex justify-between items-center mb-8">
              <div className="flex items-center gap-2">
                <h3 className="text-xl font-black text-navy-950 dark:text-white uppercase tracking-tighter">
                   Produtividade da Equipe
                </h3>
                <div className="relative">
                  <button 
                    onMouseEnter={() => setShowScoreInfo(true)}
                    onMouseLeave={() => setShowScoreInfo(false)}
                    onClick={() => setShowScoreInfo(!showScoreInfo)}
                    className="p-1 text-slate-300 hover:text-parliament-600 transition-colors"
                  >
                    <HelpCircle size={18} />
                  </button>
                  {showScoreInfo && (
                    <div className="absolute left-full top-0 ml-2 w-64 bg-navy-900 text-white p-5 rounded-2xl shadow-2xl z-50 border border-white/10 animate-in fade-in zoom-in-95">
                      <div className="flex items-center gap-2 mb-3 border-b border-white/10 pb-2">
                        <Info size={14} className="text-parliament-400" />
                        <p className="text-[10px] font-black uppercase tracking-widest">Sistema de Pontos</p>
                      </div>
                      <ul className="space-y-2">
                        <li className="flex justify-between text-[10px] font-bold"><span className="text-slate-400">Criar Contato:</span> <span className="text-emerald-400">+1 pt</span></li>
                        <li className="flex justify-between text-[10px] font-bold"><span className="text-slate-400">Criar Atendimento:</span> <span className="text-emerald-400">+1 pt</span></li>
                        <li className="flex justify-between text-[10px] font-bold"><span className="text-slate-400">Concluir Atendimento:</span> <span className="text-emerald-400">+1 pt</span></li>
                        {!isLeadership && <li className="flex justify-between text-[10px] font-bold"><span className="text-slate-400">Criar Tarefa:</span> <span className="text-emerald-400">+1 pt</span></li>}
                        {!isLeadership && <li className="flex justify-between text-[10px] font-bold"><span className="text-slate-400">Finalizar Tarefa:</span> <span className="text-emerald-400">+1 pt</span></li>}
                        {!isLeadership && <li className="flex justify-between text-[10px] font-bold"><span className="text-slate-400">Criar Documento:</span> <span className="text-emerald-400">+1 pt</span></li>}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              <span className="text-[10px] font-black text-parliament-600 bg-parliament-50 dark:bg-navy-800 px-3 py-1 rounded-full uppercase tracking-widest">Últimos {timeRange} Dias</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               {teamPerformance.slice(0, 4).map((member, idx) => (
                 <div key={member.id} className="p-5 rounded-[24px] bg-slate-50 dark:bg-navy-950 border border-transparent hover:border-parliament-100 transition-all flex items-center gap-4 group">
                    <div className="relative">
                      <div className="w-12 h-12 rounded-2xl bg-navy-900 text-white flex items-center justify-center font-black text-lg">
                        {member.name.charAt(0)}
                      </div>
                      {idx === 0 && member.score > 0 && <div className="absolute -top-2 -right-2 bg-amber-400 text-white p-1 rounded-full shadow-lg"><Star size={12} fill="currentColor"/></div>}
                    </div>
                    <div className="flex-1 min-w-0">
                       <p className="font-bold text-sm dark:text-white truncate">{member.name}</p>
                       <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 opacity-60">
                          <span className="text-[9px] font-black uppercase flex items-center gap-1" title="Contatos Criados"><Users size={10}/> {member.details.contacts}</span>
                          <span className="text-[9px] font-black uppercase flex items-center gap-1" title="Atendimentos Criados"><MessageSquare size={10}/> {member.details.attCreated}</span>
                          <span className="text-[9px] font-black uppercase flex items-center gap-1" title="Atendimentos Resolvidos"><CheckCircle2 size={10}/> {member.details.attSolved}</span>
                          {!isLeadership && <span className="text-[9px] font-black uppercase flex items-center gap-1" title="Tarefas Criadas"><Trello size={10}/> {member.details.tasksCreated}</span>}
                          {!isLeadership && <span className="text-[9px] font-black uppercase flex items-center gap-1" title="Tarefas Concluídas"><CheckSquare size={10}/> {member.details.tasksDone}</span>}
                          {!isLeadership && <span className="text-[9px] font-black uppercase flex items-center gap-1" title="Documentos Criados"><FileText size={10}/> {member.details.docs}</span>}
                       </div>
                    </div>
                    <div className="text-right shrink-0">
                       <p className="text-lg font-black text-parliament-600">{member.score}</p>
                       <p className="text-[8px] font-black text-slate-400 uppercase">Pontos</p>
                    </div>
                 </div>
               ))}
               {teamPerformance.length === 0 && (
                 <p className="col-span-full py-10 text-center text-xs text-slate-400 italic font-medium">Nenhuma atividade registrada neste período.</p>
               )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {!isLeadership && (
               <div className="bg-red-50 dark:bg-red-900/10 p-8 rounded-[40px] border border-red-100 dark:border-red-900/30">
                  <div className="flex items-center gap-3 mb-6">
                     <div className="p-2 bg-red-500 text-white rounded-xl shadow-lg shadow-red-500/20"><Clock size={18}/></div>
                     <h4 className="text-sm font-black text-red-600 dark:text-red-400 uppercase tracking-widest">Tarefas em Atraso</h4>
                  </div>
                  <div className="space-y-3">
                     {(tasks || []).filter(t => t.status !== TaskStatus.DONE && t.duedate && new Date(t.duedate + 'T12:00:00') < new Date()).slice(0, 3).map(task => (
                       <div key={task.id} onClick={() => setActiveTab('kanban')} className="bg-white dark:bg-navy-900 p-3 rounded-2xl flex items-center justify-between cursor-pointer hover:scale-[1.02] transition-all shadow-sm">
                          <p className="text-xs font-bold text-slate-700 dark:text-slate-300 truncate pr-4">{task.title}</p>
                          <span className="text-[9px] font-black text-red-500 bg-red-50 px-2 py-1 rounded-lg uppercase whitespace-nowrap">Venceu {new Date(task.duedate! + 'T12:00:00').toLocaleDateString('pt-BR')}</span>
                       </div>
                     ))}
                     {tasks.filter(t => t.status !== TaskStatus.DONE && t.duedate && new Date(t.duedate + 'T12:00:00') < new Date()).length === 0 && <p className="text-xs font-bold text-red-400/60 italic">Nenhuma tarefa atrasada. Bom trabalho!</p>}
                  </div>
               </div>
             )}

             <div className={`${isLeadership ? 'col-span-2' : ''} bg-amber-50 dark:bg-amber-900/10 p-8 rounded-[40px] border border-amber-100 dark:border-amber-900/30`}>
                <div className="flex items-center gap-3 mb-6">
                   <div className="p-2 bg-amber-500 text-white rounded-xl shadow-lg shadow-amber-500/20"><AlertCircle size={18}/></div>
                   <h4 className="text-sm font-black text-amber-600 dark:text-amber-400 uppercase tracking-widest">Atendimentos Pendentes</h4>
                </div>
                <p className="text-3xl font-black text-amber-600 mb-2">{pendingAttendancesCount}</p>
                <p className="text-xs font-medium text-amber-600/70">Demandas {isLeadership ? 'cadastradas por você' : 'da população'} que aguardam uma primeira ação.</p>
                <button onClick={() => setActiveTab('attendance')} className="mt-6 flex items-center gap-2 text-[10px] font-black text-amber-600 uppercase hover:underline">Ver todos <ArrowRight size={14}/></button>
             </div>
          </div>
        </div>

        <div className="space-y-8">
          <div className="bg-navy-900 dark:bg-navy-950 p-10 rounded-[40px] text-white flex flex-col shadow-2xl h-full">
            <div className="flex items-center gap-3 mb-10">
              <div className="p-2 bg-navy-800 text-parliament-400 rounded-xl shadow-inner"><Activity size={20} /></div>
              <h3 className="text-xl font-black tracking-tighter">{isLeadership ? 'Minhas Ações' : 'Fluxo de Atividades'}</h3>
            </div>
            
            <div className="flex-1 space-y-8 overflow-y-auto custom-scrollbar pr-2 max-h-[800px]">
               <div className="relative border-l border-navy-800 space-y-8 ml-2 pb-6">
                  {recentActivities.map((item, i) => {
                    const user = users.find(u => u.id === item.user_id);
                    return (
                      <div key={item.id || i} className="relative pl-8 animate-in slide-in-from-left-4">
                        <div className="absolute -left-[5px] top-0 w-2.5 h-2.5 rounded-full bg-white ring-4 ring-navy-900"></div>
                        <div className="space-y-1">
                            <div className="flex items-center gap-2">
                               <span className="text-[8px] font-black bg-navy-800 text-parliament-400 px-2 py-0.5 rounded uppercase tracking-widest">{item.entity_type}</span>
                               <span className="text-[9px] text-slate-500">{ (item.timestamp || (item as any).created_at) ? new Date(item.timestamp || (item as any).created_at).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}) : '--:--'}</span>
                            </div>
                            <p className="text-xs font-bold leading-tight line-clamp-1">{item.entity_name}</p>
                            <p className="text-[10px] text-slate-400 font-medium">
                               <span className="text-white">{user?.name || 'Sistema'}</span> {item.action?.toLowerCase() || 'ação realizada'}
                            </p>
                        </div>
                      </div>
                    );
                  })}
                  {recentActivities.length === 0 && <p className="text-slate-500 text-xs text-center py-10 italic">Nenhum registro de atividade recente.</p>}
               </div>
            </div>
            {!isLeadership && (
              <button onClick={() => setActiveTab('history')} className="mt-8 w-full py-4 bg-white text-navy-900 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-parliament-50 transition-all flex items-center justify-center gap-2 shadow-xl">AUDITORIA COMPLETA <ArrowRight size={14} /></button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
